import React, { useRef, useEffect, useState, useCallback } from 'react'
import { FaSearchPlus, FaSearchMinus, FaExpand, FaArrowLeft } from 'react-icons/fa'

const MATERIAL_COLORS = {
  'Aluminum': '#60a5fa', 'Steel': '#f87171', 'Titanium': '#a78bfa',
  'Stainless Steel': '#34d399', 'Copper': '#fb923c', 'Unknown': '#94a3b8',
}

export default function NetworkGraphView({ spacePath, folderName, networkData }) {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const stateRef = useRef({
    nodes: [], edges: [], level: 0,
    breadcrumb: [{ label: 'All', level: 0 }],
    transform: { x: 0, y: 0, scale: 1 },
    dragging: null, panning: false, panStart: null,
    hoveredNode: null, selectedNode: null,
    simAlpha: 1, simId: 0,
  })
  const [, forceRender] = useState(0)
  const [detail, setDetail] = useState(null)
  const [breadcrumb, setBreadcrumb] = useState([{ label: 'All', level: 0 }])
  const animRef = useRef(null)

  // Initialize graph when networkData changes
  useEffect(() => {
    if (!networkData?.nodes) return
    const state = stateRef.current
    state.nodes = networkData.nodes.map((n, i) => ({
      ...n,
      x: 400 + Math.cos(i * 0.5) * 200 + Math.random() * 50,
      y: 300 + Math.sin(i * 0.5) * 150 + Math.random() * 50,
      vx: 0, vy: 0,
      radius: n.type === 'family'
        ? Math.max(12, Math.min(40, Math.sqrt((n.spend || 0) / 5000)))
        : Math.max(8, Math.min(30, Math.sqrt((n.parts || 0) * 3))),
    }))
    state.edges = networkData.edges.map(e => ({ ...e }))
    state.level = 0
    state.breadcrumb = [{ label: 'All', level: 0 }]
    state.transform = { x: 0, y: 0, scale: 1 }
    state.simAlpha = 1
    setBreadcrumb([{ label: 'All', level: 0 }])
    startSimulation()
  }, [networkData])

  // Force simulation
  const startSimulation = useCallback(() => {
    const state = stateRef.current
    state.simAlpha = 1
    state.simId++
    const myId = state.simId

    const tick = () => {
      if (state.simId !== myId) return
      if (state.simAlpha < 0.001) {
        autoFit()
        return
      }
      state.simAlpha *= 0.98

      const nodes = state.nodes
      const edges = state.edges

      // Node index
      const nodeMap = {}
      nodes.forEach(n => { nodeMap[n.id] = n })

      // Repulsion
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j]
          let dx = b.x - a.x, dy = b.y - a.y
          let dist = Math.sqrt(dx * dx + dy * dy) || 1
          const minDist = a.radius + b.radius + 20
          if (dist < minDist) dist = minDist
          const force = 800 / (dist * dist) * state.simAlpha
          const fx = dx / dist * force, fy = dy / dist * force
          a.vx -= fx; a.vy -= fy
          b.vx += fx; b.vy += fy
        }
      }

      // Edge attraction
      edges.forEach(e => {
        const source = nodeMap[e.source]
        const target = nodeMap[e.target]
        if (!source || !target) return
        const dx = target.x - source.x, dy = target.y - source.y
        const dist = Math.sqrt(dx * dx + dy * dy) || 1
        const force = (dist - 120) * 0.005 * state.simAlpha
        const fx = dx / dist * force, fy = dy / dist * force
        source.vx += fx; source.vy += fy
        target.vx -= fx; target.vy -= fy
      })

      // Bipartite separation (families up, suppliers down)
      nodes.forEach(n => {
        if (n.type === 'family') n.vy -= 0.3 * state.simAlpha
        if (n.type === 'supplier') n.vy += 0.3 * state.simAlpha
      })

      // Centering
      const cx = nodes.reduce((s, n) => s + n.x, 0) / nodes.length
      const cy = nodes.reduce((s, n) => s + n.y, 0) / nodes.length
      nodes.forEach(n => {
        n.vx += (400 - cx) * 0.01 * state.simAlpha
        n.vy += (300 - cy) * 0.01 * state.simAlpha
      })

      // Apply velocity
      nodes.forEach(n => {
        if (state.dragging === n) return
        n.vx *= 0.85; n.vy *= 0.85
        n.x += n.vx; n.y += n.vy
      })

      draw()
      animRef.current = requestAnimationFrame(tick)
    }

    if (animRef.current) cancelAnimationFrame(animRef.current)
    animRef.current = requestAnimationFrame(tick)
  }, [])

  // Auto-fit all nodes in view
  const autoFit = useCallback(() => {
    const state = stateRef.current
    const nodes = state.nodes
    if (!nodes.length) return
    const canvas = canvasRef.current
    if (!canvas) return

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
    nodes.forEach(n => {
      minX = Math.min(minX, n.x - n.radius)
      maxX = Math.max(maxX, n.x + n.radius)
      minY = Math.min(minY, n.y - n.radius)
      maxY = Math.max(maxY, n.y + n.radius)
    })

    const w = canvas.width / window.devicePixelRatio
    const h = canvas.height / window.devicePixelRatio
    const padding = 60
    const scaleX = (w - 2 * padding) / (maxX - minX || 1)
    const scaleY = (h - 2 * padding) / (maxY - minY || 1)
    const scale = Math.min(scaleX, scaleY, 2)

    state.transform = {
      x: w / 2 - (minX + maxX) / 2 * scale,
      y: h / 2 - (minY + maxY) / 2 * scale,
      scale,
    }
    draw()
  }, [])

  // Draw
  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const dpr = window.devicePixelRatio || 1
    const w = canvas.width / dpr
    const h = canvas.height / dpr

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)

    const { x: tx, y: ty, scale } = stateRef.current.transform
    ctx.save()
    ctx.translate(tx, ty)
    ctx.scale(scale, scale)

    const state = stateRef.current
    const nodes = state.nodes
    const edges = state.edges
    const nodeMap = {}
    nodes.forEach(n => { nodeMap[n.id] = n })

    // Draw edges
    edges.forEach(e => {
      const source = nodeMap[e.source]
      const target = nodeMap[e.target]
      if (!source || !target) return

      ctx.beginPath()
      ctx.moveTo(source.x, source.y)
      ctx.lineTo(target.x, target.y)

      if (state.hoveredNode && (state.hoveredNode.id === e.source || state.hoveredNode.id === e.target)) {
        ctx.strokeStyle = '#1e5045'
        ctx.lineWidth = 1.5 / scale
        ctx.globalAlpha = 0.8
      } else if (state.hoveredNode) {
        ctx.strokeStyle = '#e5e7eb'
        ctx.lineWidth = 0.5 / scale
        ctx.globalAlpha = 0.3
      } else {
        ctx.strokeStyle = '#9ca3af'
        ctx.lineWidth = 0.8 / scale
        ctx.globalAlpha = 0.5
      }
      ctx.stroke()
      ctx.globalAlpha = 1
    })

    // Draw nodes
    nodes.forEach(n => {
      const dimmed = state.hoveredNode && state.hoveredNode.id !== n.id &&
        !edges.some(e => (e.source === state.hoveredNode.id && e.target === n.id) ||
                        (e.target === state.hoveredNode.id && e.source === n.id))

      ctx.beginPath()
      ctx.arc(n.x, n.y, n.radius, 0, Math.PI * 2)

      if (n.type === 'family') {
        ctx.fillStyle = MATERIAL_COLORS[n.material] || '#94a3b8'
      } else {
        ctx.fillStyle = '#1e5045'
      }

      ctx.globalAlpha = dimmed ? 0.2 : 0.85
      ctx.fill()
      ctx.globalAlpha = 1

      // Border
      if (state.hoveredNode?.id === n.id || state.selectedNode?.id === n.id) {
        ctx.strokeStyle = '#f26522'
        ctx.lineWidth = 2.5 / scale
        ctx.stroke()
      }

      // Label
      if (n.radius * scale > 15 || state.hoveredNode?.id === n.id) {
        const fontSize = Math.max(9, Math.min(12, n.radius * 0.7))
        ctx.font = `500 ${fontSize}px -apple-system, sans-serif`
        ctx.fillStyle = dimmed ? '#d1d5db' : '#111827'
        ctx.textAlign = 'center'
        ctx.globalAlpha = dimmed ? 0.3 : 1
        ctx.fillText(
          n.label.length > 18 ? n.label.slice(0, 18) + '…' : n.label,
          n.x, n.y + n.radius + fontSize + 3
        )
        ctx.globalAlpha = 1
      }
    })

    ctx.restore()
  }, [])

  // Canvas sizing
  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current
      const container = containerRef.current
      if (!canvas || !container) return
      const dpr = window.devicePixelRatio || 1
      const rect = container.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      canvas.style.width = rect.width + 'px'
      canvas.style.height = rect.height + 'px'
      draw()
    }
    resize()
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [draw])

  // Mouse interactions
  const screenToWorld = (cx, cy) => {
    const { x: tx, y: ty, scale } = stateRef.current.transform
    return { x: (cx - tx) / scale, y: (cy - ty) / scale }
  }

  const findNode = (wx, wy) => {
    const nodes = stateRef.current.nodes
    for (let i = nodes.length - 1; i >= 0; i--) {
      const n = nodes[i]
      const dx = wx - n.x, dy = wy - n.y
      if (dx * dx + dy * dy <= n.radius * n.radius) return n
    }
    return null
  }

  const handleMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top
    const { x: wx, y: wy } = screenToWorld(cx, cy)
    const state = stateRef.current

    if (state.dragging) {
      state.dragging.x = wx
      state.dragging.y = wy
      state.dragging.vx = 0
      state.dragging.vy = 0
      draw()
      return
    }

    if (state.panning && state.panStart) {
      const dx = cx - state.panStart.x
      const dy = cy - state.panStart.y
      state.transform.x += dx
      state.transform.y += dy
      state.panStart = { x: cx, y: cy }
      draw()
      return
    }

    const node = findNode(wx, wy)
    if (node !== state.hoveredNode) {
      state.hoveredNode = node
      canvasRef.current.style.cursor = node ? 'pointer' : 'grab'
      draw()
    }
  }

  const handleMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top
    const { x: wx, y: wy } = screenToWorld(cx, cy)
    const state = stateRef.current
    const node = findNode(wx, wy)

    if (node) {
      state.dragging = node
      canvasRef.current.style.cursor = 'grabbing'
    } else {
      state.panning = true
      state.panStart = { x: cx, y: cy }
      canvasRef.current.style.cursor = 'grabbing'
    }
  }

  const handleMouseUp = () => {
    const state = stateRef.current
    if (state.dragging) {
      state.dragging = null
    }
    state.panning = false
    state.panStart = null
    canvasRef.current.style.cursor = state.hoveredNode ? 'pointer' : 'grab'
  }

  const handleClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top
    const { x: wx, y: wy } = screenToWorld(cx, cy)
    const node = findNode(wx, wy)
    if (node) {
      stateRef.current.selectedNode = node
      setDetail(node)
      forceRender(n => n + 1)
    }
  }

  const handleWheel = (e) => {
    e.preventDefault()
    const state = stateRef.current
    const rect = canvasRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    const newScale = state.transform.scale * delta

    // Zoom towards cursor
    state.transform.x = cx - (cx - state.transform.x) * delta
    state.transform.y = cy - (cy - state.transform.y) * delta
    state.transform.scale = newScale
    draw()
  }

  const zoom = (factor) => {
    const state = stateRef.current
    const canvas = canvasRef.current
    const dpr = window.devicePixelRatio || 1
    const cx = canvas.width / dpr / 2
    const cy = canvas.height / dpr / 2
    state.transform.x = cx - (cx - state.transform.x) * factor
    state.transform.y = cy - (cy - state.transform.y) * factor
    state.transform.scale *= factor
    draw()
  }

  return (
    <div className="flex gap-4">
      {/* Graph panel */}
      <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm relative overflow-hidden" style={{ minHeight: 550 }}>
        {/* Breadcrumb */}
        <div className="absolute top-3 left-3 z-10 flex items-center gap-2 bg-white/95 px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm text-xs">
          {breadcrumb.length > 1 && (
            <button
              onClick={() => {
                const prev = breadcrumb[breadcrumb.length - 2]
                setBreadcrumb(breadcrumb.slice(0, -1))
              }}
              className="px-2 py-0.5 bg-[#1e5045] text-white rounded text-[10px] font-semibold"
            >
              <FaArrowLeft size={8} />
            </button>
          )}
          {breadcrumb.map((b, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <span className="text-gray-300">›</span>}
              <span className={i === breadcrumb.length - 1 ? 'font-semibold text-[#1e5045]' : 'text-gray-500'}>
                {b.label}
              </span>
            </span>
          ))}
        </div>

        {/* Controls */}
        <div className="absolute top-3 right-3 z-10 flex flex-col gap-1">
          <button onClick={() => zoom(1.3)} className="w-7 h-7 bg-white border border-gray-200 rounded flex items-center justify-center text-gray-600 hover:border-[#1e5045] hover:text-[#1e5045]">
            <FaSearchPlus size={12} />
          </button>
          <button onClick={() => zoom(0.7)} className="w-7 h-7 bg-white border border-gray-200 rounded flex items-center justify-center text-gray-600 hover:border-[#1e5045] hover:text-[#1e5045]">
            <FaSearchMinus size={12} />
          </button>
          <button onClick={autoFit} className="w-7 h-7 bg-white border border-gray-200 rounded flex items-center justify-center text-gray-600 hover:border-[#1e5045] hover:text-[#1e5045]">
            <FaExpand size={12} />
          </button>
        </div>

        {/* Canvas */}
        <div ref={containerRef} className="w-full h-full" style={{ minHeight: 550 }}>
          <canvas
            ref={canvasRef}
            onMouseMove={handleMouseMove}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onClick={handleClick}
            onWheel={handleWheel}
            style={{ cursor: 'grab' }}
          />
        </div>

        {/* Legend */}
        <div className="absolute bottom-3 left-3 flex flex-wrap gap-2 bg-white/90 px-3 py-2 rounded-lg border border-gray-200">
          {Object.entries(MATERIAL_COLORS).filter(([k]) => k !== 'Unknown').map(([mat, color]) => (
            <div key={mat} className="flex items-center gap-1 text-[10px] text-gray-600">
              <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: color }} />
              {mat}
            </div>
          ))}
          <div className="flex items-center gap-1 text-[10px] text-gray-600">
            <span className="w-3 h-3 rounded-full inline-block bg-[#1e5045]" />
            Supplier
          </div>
        </div>
      </div>

      {/* Side panel */}
      {detail && (
        <div className="w-72 flex-shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sticky top-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800 truncate">{detail.label || detail.full_name}</h3>
              <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                detail.type === 'family' ? 'bg-blue-100 text-blue-700' : 'bg-[#1e5045]/10 text-[#1e5045]'
              }`}>{detail.type}</span>
            </div>
            <div className="space-y-2 text-xs">
              {detail.type === 'family' && (
                <>
                  <Row label="Parts" value={detail.parts} />
                  <Row label="Suppliers" value={detail.supplier_count} />
                  <Row label="Method" value={detail.method} />
                  <Row label="Material" value={detail.material} />
                  <Row label="Spend" value={`€${((detail.spend || 0) / 1000).toFixed(0)}K`} accent />
                </>
              )}
              {detail.type === 'supplier' && (
                <>
                  <Row label="Full Name" value={detail.full_name} />
                  <Row label="Parts Supplied" value={detail.parts} />
                  <Row label="Families" value={detail.family_count} />
                  <Row label="Total Spend" value={`€${((detail.spend || 0) / 1000).toFixed(0)}K`} accent />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between items-center py-1 border-b border-gray-50">
      <span className="text-gray-500">{label}</span>
      <span className={`font-medium ${accent ? 'text-[#1e5045]' : 'text-gray-800'}`}>{value || '—'}</span>
    </div>
  )
}
